import { AddOutlined, PersonOutline, BusinessOutlined } from '@mui/icons-material';
import { useState } from 'react';

import type { BeneficialOwner, CreateBeneficialOwnerDto } from '@/types/adminOnboarding';

import { BeneficialOwnerCard } from '../BeneficialOwnerCard';
import { BeneficialOwnerForm } from '../BeneficialOwnerForm';
import { OwnershipSummaryBar } from '../OwnershipSummaryBar';

interface BeneficialOwnersStepProps {
  ubos: BeneficialOwner[];
  onAddUbo: (data: CreateBeneficialOwnerDto) => void;
  onDeleteUbo: (id: string) => void;
  onUploadDoc: (uboId: string, documentType: string, file: File) => Promise<void> | void;
  onDeleteDoc?: (documentId: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  isAdding?: boolean;
}

type AddingType = 'individual' | 'company' | null;

export const BeneficialOwnersStep: React.FC<BeneficialOwnersStepProps> = ({
  ubos,
  onAddUbo,
  onDeleteUbo,
  onUploadDoc,
  onDeleteDoc,
  onSubmit,
  onBack,
  isSubmitting = false,
  isAdding = false,
}) => {
  const [addingType, setAddingType] = useState<AddingType>(null);

  const topLevelUbos = ubos.filter(u => !u.parentId);
  const totalOwnership = topLevelUbos.reduce((sum, u) => sum + (Number(u.ownershipPercentage) || 0), 0);
  const companiesWithoutChildren = topLevelUbos.filter(
    u => u.type === 'company' && (!u.children || u.children.length === 0)
  );

  const handleAddChild = (_parentId: string, data: CreateBeneficialOwnerDto) => {
    onAddUbo(data);
  };

  return (
    <div className="space-y-6">
      <OwnershipSummaryBar totalPercentage={totalOwnership} />

      {topLevelUbos.length > 0 && (
        <div className="space-y-1">
          {topLevelUbos.map(ubo => (
            <BeneficialOwnerCard
              key={ubo.id}
              ubo={ubo}
              onDelete={onDeleteUbo}
              onAddChild={ubo.type === 'company' ? handleAddChild : undefined}
              onUploadDoc={onUploadDoc}
              onDeleteDoc={onDeleteDoc}
            />
          ))}
        </div>
      )}

      {addingType ? (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-800 mb-3">
            Nuevo beneficiario — {addingType === 'individual' ? 'persona física' : 'persona moral'}
          </p>
          <BeneficialOwnerForm
            defaultType={addingType}
            onSubmit={data => {
              onAddUbo(data);
              setAddingType(null);
            }}
            onCancel={() => setAddingType(null)}
            isLoading={isAdding}
          />
        </div>
      ) : (
        <div className="flex gap-3">
          <button
            onClick={() => setAddingType('individual')}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            <PersonOutline fontSize="small" />
            Agregar persona física
          </button>
          <button
            onClick={() => setAddingType('company')}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BusinessOutlined fontSize="small" />
            Agregar persona moral
          </button>
        </div>
      )}

      {topLevelUbos.length === 0 && !addingType && (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <AddOutlined className="text-gray-300 mb-2" style={{ fontSize: 40 }} />
          <p className="text-sm text-gray-500">No hay beneficiarios finales registrados.</p>
          <p className="text-xs text-gray-400 mt-1">
            Agrega al menos un beneficiario con participación ≥25%.
          </p>
        </div>
      )}

      {companiesWithoutChildren.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
          {companiesWithoutChildren.map(c => (
            <p key={c.id}>
              La empresa <strong>{c.name}</strong> debe tener al menos una persona física como
              beneficiario.
            </p>
          ))}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Anterior
        </button>
        <button
          onClick={onSubmit}
          disabled={isSubmitting || topLevelUbos.length === 0 || companiesWithoutChildren.length > 0}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? 'Enviando...' : 'Enviar para revisión'}
        </button>
      </div>

      {topLevelUbos.length === 0 && (
        <p className="text-xs text-amber-600 text-center">
          Agrega al menos un beneficiario final para continuar.
        </p>
      )}
    </div>
  );
};
